/**
 * VoorraadList - Voorraad overzicht met categoriefilters (zoals motoronderdelen)
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiFetch from '../../services/api';
// Removed werkorders service import - no longer needed
import ConfirmDialog from '../../components/ConfirmDialog';
import ErrorDialog from '../../components/ErrorDialog';
import { useAuth } from '../../context/AuthContext';
import './Voorraad.css';

interface VoorraadCategorie {
  id: number;
  code: string;
  naam: string;
  volgorde: number;
  actief: boolean;
}

interface VoorraadGroep {
  id: number;
  parent_id: number | null;
  categorie_id: number;
  naam: string;
  niveau: number;
  volgorde: number;
  actief: boolean;
  children_count: number;
  items_count: number;
}

interface VoorraadItem {
  id: number;
  groep_id: number;
  onderdeel_type: string;
  fabrikant: string | null;
  model: string | null;
  leverancier: string | null;
  artikelnummer: string | null;
  omschrijving: string | null;
  levensduur_maanden: number | null;
  aantal_voorraad: number;
  min_voorraad: number | null;
  inkoopprijs: number | null;
  verkoopprijs: number | null;
  // Motor specifieke velden
  vermogen_pk?: number | null;
  vermogen_kw?: number | null;
  cilinders?: number | null;
  brandstof_type?: string | null;
  koelsysteem?: string | null;
  motor_model_id?: number | null;
  status: string;
}

const VoorraadList = () => {
  const [categorieen, setCategorieen] = useState<VoorraadCategorie[]>([]);
  const [groepen, setGroepen] = useState<VoorraadGroep[]>([]);
  const [items, setItems] = useState<VoorraadItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<VoorraadItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroepId, setSelectedGroepId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [tableLeft, setTableLeft] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);

  // Removed boeken modal state - no longer needed
  const { hasPermission } = useAuth();

  const categorieCode = searchParams.get('categorie');
  const zoekParam = searchParams.get('zoek');


  // Zet zoekterm vanuit URL parameter
  useEffect(() => {
    if (zoekParam) {
      setSearchTerm(zoekParam);
    }
  }, [zoekParam]);

  useEffect(() => {
    loadData();
  }, [categorieCode]);

  // Removed werkorders loading - no longer needed

  // Removed boeken modal functions - no longer needed

  useEffect(() => {
    // Filter items op basis van zoekterm en geselecteerde groep
    let filtered = items;

    // Filter op groep
    if (selectedGroepId !== null) {
      /* Ook de subgroepen meetellen. Stond op een exacte match, waardoor "Filters"
         nul resultaten gaf zodra de filters in Luchtfilters, Brandstoffilters en
         Oliefilters zaten. Aangepast 25-09-2026. */
      const ids = new Set<number>([selectedGroepId]);
      let gegroeid = true;
      while (gegroeid) {
        gegroeid = false;
        groepen.forEach(g => {
          if (g.parent_id !== null && ids.has(g.parent_id) && !ids.has(g.id)) {
            ids.add(g.id);
            gegroeid = true;
          }
        });
      }
      filtered = filtered.filter(item => ids.has(item.groep_id));
    }

    // Filter op zoekterm
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.onderdeel_type.toLowerCase().includes(term) ||
        (item.fabrikant && item.fabrikant.toLowerCase().includes(term)) ||
        (item.model && item.model.toLowerCase().includes(term)) ||
        (item.leverancier && item.leverancier.toLowerCase().includes(term)) ||
        (item.artikelnummer && item.artikelnummer.toLowerCase().includes(term))
      );
    }

    setFilteredItems(filtered);
  }, [searchTerm, items, selectedGroepId, groepen]);

  // Reset groep selectie als categorie verandert
  useEffect(() => {
    setSelectedGroepId(null);
  }, [categorieCode]);

  // Sticky header scroll listener
  useEffect(() => {
    if (!items.length) return; // Wait for items to load

    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const rect = tableHeaderRef.current.getBoundingClientRect();
        const shouldBeSticky = rect.bottom <= 185; // Show sticky when original scrolls to position 185

        // Always update position when scrolling
        setTableLeft(rect.left);
        setTableWidth(rect.width);
        setIsHeaderSticky(shouldBeSticky);
      }
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      handleScroll(); // Check initial position
    }, 100);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [items]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Laad alle categorieën
      const categorieenData = await apiFetch('/api/v1/voorraad/categorieen');
      setCategorieen(categorieenData);

      if (categorieCode) {
        // Laad groepen voor geselecteerde categorie (inclusief subgroepen)
        const categorie = categorieenData.find((c: VoorraadCategorie) => c.code === categorieCode);
        if (categorie) {
          const groepenData = await apiFetch(`/api/v1/voorraad/groepen?categorie_id=${categorie.id}&alle=true`);
          setGroepen(groepenData);

          // Laad alle items voor deze categorie in één call
          const allItems = await apiFetch(`/api/v1/voorraad/items/all?categorie_id=${categorie.id}`);
          setItems(allItems);
        }
      } else {
        // Laad ALLE items in één call
        setGroepen([]);
        const allItems = await apiFetch('/api/v1/voorraad/items/all');
        setItems(allItems);
      }
    } catch (err: any) {
      setError(err.message || 'Fout bij laden van voorraad');
      console.error('Error loading voorraad:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    try {
      setDeleting(true);
      /* Motoren stonden vroeger in een eigen tabel en werden in deze lijst
         meegeleverd met een id-offset van 1.000.000, zodat ze niet botsten met
         producten. Sinds 23-09-2026 zijn motoren gewoon producten en is die truc
         weg — evenals het endpoint /motor-modellen, dat trouwens nooit een DELETE had. */
      await apiFetch(`/api/v1/voorraad/items/${itemId}`, {
        method: 'DELETE'
      });
      // Verwijder item uit lijst
      setItems(items.filter(item => item.id !== itemId));
      setDeleteItemId(null);
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen van item');
    } finally {
      setDeleting(false);
    }
  };

  /* De groependropdown als boom, subgroepen ingesprongen met een streepje. Zelfde
     patroon als OnderdeelForm.tsx al gebruikt voor zijn groepkeuze. */
  const groepenBoom = (parentId: number | null = null, diepte = 0): JSX.Element[] =>
    groepen
      .filter(g => (g.parent_id ?? null) === parentId)
      .flatMap(g => [
        <option key={g.id} value={g.id.toString()}>
          {'\u2500 '.repeat(diepte)}{g.naam} ({g.items_count} items)
        </option>,
        ...groepenBoom(g.id, diepte + 1),
      ]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Producten</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          Producten laden...
        </div>
      </div>
    );
  }

  // Bepaal de paginatitel op basis van geselecteerde categorie
  const getPageTitle = () => {
    if (categorieCode) {
      const categorie = categorieen.find(c => c.code === categorieCode);
      return categorie ? categorie.naam : 'Producten';
    }
    return 'Producten';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{getPageTitle()}</h1>
        {hasPermission('producten_aanmaken') && (
          <button
            className="btn-primary"
            onClick={() => navigate('/onderdelen/new')}
          >
            + Nieuw Product
          </button>
        )}
      </div>

      {/* Zoekbalk en Filter dropdown op één regel */}
      <div className="search-bar">
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <input
            type="text"
            placeholder="Zoek op type, fabrikant, model, leverancier of artikelnummer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-results">
            {filteredItems.length} {filteredItems.length === 1 ? 'resultaat' : 'resultaten'}
          </span>
        </div>

        {/* Filter dropdown - toont categorieën of groepen afhankelijk van context */}
        <select
          id="filter-select"
          value={categorieCode ? (selectedGroepId?.toString() || 'all') : ''}
          onChange={(e) => {
            if (categorieCode) {
              // We zitten in een categorie
              if (e.target.value === 'all') {
                // Alle items in deze categorie
                setSelectedGroepId(null);
              } else if (e.target.value === 'back') {
                // Terug naar alle voorraad
                navigate('/voorraad');
              } else {
                // Specifieke groep geselecteerd - navigeer naar groep detail pagina
                navigate(`/voorraad/groep/${e.target.value}`);
              }
            } else {
              // We zitten op alle voorraad - navigeer naar categorie
              if (e.target.value) {
                navigate(`/voorraad?categorie=${e.target.value}`);
              }
            }
          }}
          style={{
            padding: '0.625rem 1rem',
            fontSize: '0.9375rem',
            border: '1px solid #cbd5e1',
            borderRadius: '6px',
            backgroundColor: 'white',
            color: '#1e293b',
            cursor: 'pointer',
            minWidth: '250px',
            outline: 'none'
          }}
        >
          {categorieCode ? (
            // In een categorie - de hele boom, subgroepen ingesprongen
            <>
              <option value="all">Selecteer groep...</option>
              {groepenBoom()}
            </>
          ) : (
            // Op hoofdniveau - toon categorieën
            <>
              <option value="">Alle Producten</option>
              {categorieen.map(cat => (
                <option key={cat.id} value={cat.code}>
                  {cat.naam}
                </option>
              ))}
            </>
          )}
        </select>
      </div>

      {/* Vaste header rij (altijd zichtbaar) */}
      {items.length > 0 && (
        <div ref={tableHeaderRef} className="table-header" style={{
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          padding: '0.75rem 1rem',
          marginTop: '1rem',
          borderRadius: '8px 8px 0 0',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Type</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Fabrikant</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Model</div>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Leverancier</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Artikelnr.</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Voorraad</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Status</div>
          <div style={{ flex: '1.8', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {!categorieCode && items.length === 0 ? (
        <div style={{
          padding: '3rem',
          backgroundColor: '#f1f5f9',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <h3 style={{ marginBottom: '1rem' }}>Geen producten</h3>
          <p style={{ color: '#64748b' }}>
            Er zijn nog geen items in de producten. Selecteer een categorie om te beginnen.
          </p>
        </div>
      ) : categorieCode && groepen.length === 0 && items.length === 0 ? (
        <div style={{
          padding: '2rem',
          backgroundColor: '#f1f5f9',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p>Nog geen groepen in deze categorie.</p>
          <button
            className="btn-primary"
            onClick={() => navigate('/voorraad/nieuwe-subgroep')}
            style={{ marginTop: '1rem' }}
          >
            Voeg eerste groep toe
          </button>
        </div>
      ) : (
        <>
          {/* Sticky header overlay (duplicaat) */}
          {isHeaderSticky && (
            <div className="table-header" style={{
              position: 'fixed',
              top: '185px',
              left: `${tableLeft}px`,
              width: `${tableWidth}px`,
              zIndex: 1000,
              display: 'flex',
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #dee2e6',
              borderRadius: '8px 8px 0 0',
              padding: '0.75rem 1rem',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#495057'
            }}>
              <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Type</div>
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Fabrikant</div>
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Model</div>
              <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Leverancier</div>
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Artikelnr.</div>
              <div style={{ width: '70px', flexShrink: 0, paddingRight: '1rem' }}>Voorraad</div>
              <div style={{ width: '70px', flexShrink: 0, paddingRight: '1rem' }}>Status</div>
              <div style={{ width: '320px', flexShrink: 0 }}>Acties</div>
            </div>
          )}

          {/* Items lijst - Desktop tabel */}
          {items.length > 0 && (
            <div className="desktop-table">
              <div style={{
                background: 'white',
                borderRadius: '0 0 8px 8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                {filteredItems.map(item => (
                  <div key={item.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    borderBottom: '1px solid #dee2e6',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <div style={{ flex: '1.2', minWidth: 0, fontWeight: 500, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.onderdeel_type}</div>
                    <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.fabrikant || '-'}</div>
                    <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.model || '-'}</div>
                    <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.leverancier || '-'}</div>
                    <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.artikelnummer || '-'}</div>
                    <div style={{ width: '70px', flexShrink: 0, fontWeight: 500, paddingRight: '1rem' }}>{item.aantal_voorraad}</div>
                    <div style={{ width: '70px', flexShrink: 0, paddingRight: '1rem', textAlign: 'left' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '10px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        minWidth: '50px',
                        textAlign: 'center',
                        background: item.status === 'kritiek' ? '#fee2e2' : item.status === 'waarschuwing' ? '#fef3c7' : '#d1fae5',
                        color: item.status === 'kritiek' ? '#991b1b' : item.status === 'waarschuwing' ? '#92400e' : '#065f46'
                      }}>
                        {item.status === 'kritiek' ? 'Kritiek' : item.status === 'waarschuwing' ? 'Laag' : 'OK'}
                      </span>
                    </div>
                    <div className="actions" style={{ width: '320px', flexShrink: 0 }}>
                      {hasPermission('producten_bewerken') && (
                        <button
                          className="btn-small btn-secondary"
                          onClick={() => navigate(`/onderdelen/${item.id}`)}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Bewerken
                        </button>
                      )}
                      <button
                        className="btn-small btn-success"
                        onClick={() => navigate(`/onderdelen/${item.id}/documenten`)}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Documenten
                      </button>
                      {hasPermission('producten_verwijderen') && (
                        <button
                          className="btn-small btn-danger"
                          onClick={() => setDeleteItemId(item.id)}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Verwijder
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Items lijst - Mobile tegels */}
          {items.length > 0 && (
            <div className="mobile-cards">
              {filteredItems.map(item => (
                <div key={item.id} className="product-card">
                  <div className="product-card-header">
                    <span className="product-type">{item.onderdeel_type}</span>
                    <span className={`status-badge status-${item.status}`}>
                      {item.status === 'kritiek' ? 'Kritiek' : item.status === 'waarschuwing' ? 'Laag' : 'OK'}
                    </span>
                  </div>
                  <div className="product-card-body">
                    {item.fabrikant && <div className="product-info"><span className="label">Fabrikant:</span> {item.fabrikant}</div>}
                    {item.model && <div className="product-info"><span className="label">Model:</span> {item.model}</div>}
                    {item.leverancier && <div className="product-info"><span className="label">Leverancier:</span> {item.leverancier}</div>}
                    {item.artikelnummer && <div className="product-info"><span className="label">Artikelnr:</span> {item.artikelnummer}</div>}
                    <div className="product-info"><span className="label">Voorraad:</span> <strong>{item.aantal_voorraad}</strong></div>
                  </div>
                  <div className="product-card-actions">
                    <button
                      className="btn-small btn-success"
                      onClick={() => navigate(`/onderdelen/${item.id}/documenten`)}
                    >
                      Docs
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {items.length > 0 && (
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#f0f9ff',
          borderRadius: '8px',
          fontSize: '0.9rem'
        }}>
          <strong>Totaal:</strong> {items.filter(i => i.aantal_voorraad > 0).length} Producten (van {items.length} producten) op voorraad totaal aantal {items.reduce((sum, i) => sum + i.aantal_voorraad, 0)}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteItemId}
        title="Item Verwijderen"
        message={(() => {
          const item = items.find(i => i.id === deleteItemId);
          if (!item) return 'Weet je zeker dat je dit item wilt verwijderen?';
          const naam = [item.fabrikant, item.model].filter(Boolean).join(' ') || item.onderdeel_type;
          return `Weet je zeker dat je de "${naam}" wilt verwijderen?`;
        })()}
        confirmText={deleting ? 'Verwijderen...' : 'Verwijderen'}
        cancelText="Annuleren"
        onConfirm={() => deleteItemId && handleDeleteItem(deleteItemId)}
        onCancel={() => setDeleteItemId(null)}
        danger={true}
      />

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />

      {/* Removed boeken modal and success notification - no longer needed */}

    </div>
  );
};

export default VoorraadList;